import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ConvoysPage from "./pages/ConvoysPage";
import ROOPage from "./pages/ROOPage";
import SinistrosPage from "./pages/SinistrosPage";
import PAPage from "./pages/PAPage";
import ChecklistPage from "./pages/ChecklistPage";
import LPTEPage from "./pages/LPTEPage";
import AccessLogsPage from "./pages/AccessLogsPage";
import ReportsPage from "./pages/ReportsPage";
import UsersPage from "./pages/UsersPage";
import DocumentosBordoPage from "./pages/DocumentosBordoPage";

// Operacional Pages
import ManutencaoPage from "./pages/ManutencaoPage";
import EmpurradoresPage from "./pages/EmpurradoresPage";
import BalsasPage from "./pages/BalsasPage";
import DestinosPage from "./pages/DestinosPage";
import DefeitosPage from "./pages/DefeitosPage";
import ViagensPage from "./pages/ViagensPage";
import DashboardOperacionalPage from "./pages/DashboardOperacionalPage";
import BoletinsPage from "./pages/BoletinsPage";
import RDOPage from "./pages/RDOPage";
import NaoConformidadesPage from "./pages/NaoConformidadesPage";
import ControleCicloPage from "./pages/ControleCicloPage";
import TripulantesPage from "./pages/TripulantesPage";
import AvaliacaoBunkerPage from "./pages/AvaliacaoBunkerPage";
import ControleOperacionalPage from "./pages/ControleOperacionalPage";
import DesempenhoPLRPage from "./pages/DesempenhoPLRPage";
import RastreamentoAISPage from "./pages/RastreamentoAISPage";
import GeofencesPage from "./pages/GeofencesPage";
import { ErrorBoundary } from "./components/ErrorBoundary";
import PlanoManutencaoPage from "./pages/PlanoManutencaoPage";
import RankingEmbarcacoesPage from "./pages/RankingEmbarcacoesPage";
import EscalaServicosPage from "./pages/EscalaServicosPage";
import AcompanhamentoViagemPage from "./pages/AcompanhamentoViagemPage";
import PassagemServicoPage from "./pages/PassagemServicoPage";
import RankingPassagensPage from "./pages/RankingPassagensPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { isAuthenticated, loading, hasRole } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="animate-pulse text-cyan-400 font-mono">Carregando...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !hasRole(allowedRoles)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route Component
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <div className="animate-pulse text-cyan-400 font-mono">Carregando...</div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />

            {/* Protected Routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/convoys" 
                element={
                    <ProtectedRoute>
                        <ConvoysPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/roos" 
                element={
                    <ProtectedRoute>
                        <ROOPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/sinistros" 
                element={
                    <ProtectedRoute>
                        <SinistrosPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/pas" 
                element={
                    <ProtectedRoute>
                        <PAPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/checklists" 
                element={
                    <ProtectedRoute>
                        <ChecklistPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/lptes" 
                element={
                    <ProtectedRoute>
                        <LPTEPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/access-logs" 
                element={
                    <ProtectedRoute>
                        <AccessLogsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'gestor', 'consulta']}>
                        <ReportsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/users" 
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <UsersPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/documentos-bordo" 
                element={
                    <ProtectedRoute>
                        <DocumentosBordoPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <div className="text-white">Configurações - Em desenvolvimento</div>
                    </ProtectedRoute>
                } 
            />

            {/* Operacional Routes */}
            <Route 
                path="/operacional/viagens" 
                element={
                    <ProtectedRoute>
                        <ViagensPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/rdo" 
                element={
                    <ProtectedRoute>
                        <RDOPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/empurradores" 
                element={
                    <ProtectedRoute>
                        <EmpurradoresPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/balsas" 
                element={
                    <ProtectedRoute>
                        <BalsasPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/destinos" 
                element={
                    <ProtectedRoute>
                        <DestinosPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/defeitos" 
                element={
                    <ProtectedRoute>
                        <DefeitosPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/manutencao" 
                element={
                    <ProtectedRoute>
                        <ManutencaoPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/dashboard" 
                element={
                    <ProtectedRoute allowedRoles={['admin', 'gestor', 'consulta']}>
                        <DashboardOperacionalPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/operacional/boletins" 
                element={
                    <ProtectedRoute>
                        <BoletinsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/nao-conformidades" 
                element={
                    <ProtectedRoute>
                        <NaoConformidadesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/controle-ciclo" 
                element={
                    <ProtectedRoute>
                        <ControleCicloPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/passagem-servico" 
                element={
                    <ProtectedRoute>
                        <PassagemServicoPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/ranking-passagens" 
                element={
                    <ProtectedRoute>
                        <RankingPassagensPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/tripulantes" 
                element={
                    <ProtectedRoute>
                        <TripulantesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/avaliacao-bunker" 
                element={
                    <ProtectedRoute>
                        <AvaliacaoBunkerPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/controle-operacional" 
                element={
                    <ProtectedRoute>
                        <ControleOperacionalPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/plano-manutencao" 
                element={
                    <ProtectedRoute>
                        <PlanoManutencaoPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/ranking-embarcacoes" 
                element={
                    <ProtectedRoute>
                        <RankingEmbarcacoesPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/escala-servicos" 
                element={
                    <ProtectedRoute>
                        <EscalaServicosPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/acompanhamento-viagem" 
                element={
                    <ProtectedRoute>
                        <AcompanhamentoViagemPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/desempenho-plr" 
                element={
                    <ProtectedRoute>
                        <DesempenhoPLRPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/rastreamento-ais" 
                element={
                    <ProtectedRoute>
                        <ErrorBoundary>
                            <RastreamentoAISPage />
                        </ErrorBoundary>
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/geofences" 
                element={
                    <ProtectedRoute>
                        <ErrorBoundary>
                            <GeofencesPage />
                        </ErrorBoundary>
                    </ProtectedRoute>
                } 
            />

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    // Reforça notranslate no <html> em runtime (defesa contra extensões que removem)
    React.useEffect(() => {
        const html = document.documentElement;
        html.setAttribute('translate', 'no');
        html.classList.add('notranslate');
    }, []);

    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster 
                        position="top-right"
                        toastOptions={{
                            style: {
                                background: '#0f172a',
                                border: '1px solid #1e293b',
                                color: '#f8fafc'
                            }
                        }}
                    />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
